import type { ProjectRole } from '../types/data-models';

/**
 * Client-side mirror of `backend/src/auth/permissions/permissions.ts`.
 *
 * This copy exists ONLY to avoid rendering buttons that would 403. It is not a
 * security boundary — the backend enforces every one of these independently,
 * and a user who edits this file gains nothing. Keep the two in sync
 * deliberately; the backend copy is the one that matters.
 */
export type Permission =
  | 'project:view'
  | 'comment:create'
  | 'comment:resolve'
  | 'project:edit'
  | 'document:generate'
  | 'assistant:write'
  | 'project:manage_members'
  | 'project:delete';

export const ROLE_PERMISSIONS: Record<ProjectRole, readonly Permission[]> = {
  OWNER: [
    'project:view',
    'comment:create',
    'comment:resolve',
    'project:edit',
    'document:generate',
    'assistant:write',
    'project:manage_members',
    'project:delete',
  ],
  REVIEWER: ['project:view', 'comment:create'],
  VIEWER: ['project:view'],
};

/**
 * Permissions that additionally require membership of the owning workspace.
 * An invited OWNER may edit and invite, but not delete the host's project.
 */
export const TENANT_MEMBER_ONLY: readonly Permission[] = ['project:delete'];

export function can(role: ProjectRole | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/** Human-readable role label for chips and menus. */
export const ROLE_LABEL: Record<ProjectRole, string> = {
  OWNER: 'Owner',
  REVIEWER: 'Reviewer',
  VIEWER: 'Viewer',
};

export const ROLE_DESCRIPTION: Record<ProjectRole, string> = {
  OWNER: 'View, edit and generate content, and invite others.',
  REVIEWER: 'View the project and leave comments. No content edits.',
  VIEWER: 'View the project only.',
};
